// The Module Builder's working copy of a module, and the edits made to it.
// Every function returns a new draft, so React state updates stay simple.
import { DEFAULT_SECTION, OPTION_TYPES, sectionKeyOf, settingsOf } from "./custom-module-utils";

export const toKey = (label) => {
  const key = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return (/^[a-z]/.test(key) ? key : `f_${key}`).slice(0, 50);
};

// Two fields can carry the same label; their storage keys still have to differ.
const uniqueKey = (base, fields, skipIndex) => {
  const taken = new Set(fields.filter((_, i) => i !== skipIndex).map((field) => field.key));
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base.slice(0, 45)}_${n}`;
  return key;
};

export const splitOptions = (text) =>
  (text ?? "").split(",").map((option) => option.trim()).filter(Boolean);

export const newSectionKey = () => `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Builder-only identity for a field card. The storage key can change while a
// new field's label is typed, so it cannot serve as the React key when cards move.
let lastUid = 0;
const nextUid = () => ++lastUid;

export const emptyField = (section = DEFAULT_SECTION.key) => ({
  uid: nextUid(),
  key: "",
  label: "",
  type: "text",
  required: false,
  unique: false,
  hidden: false,
  optionsText: "",
  option_colors: {},
  source: "",
  section,
  width: "normal",
  placeholder: "",
  help: "",
  default: null,
  min: "",
  max: "",
  max_length: "",
  subfields: [],
  max_rows: "",
  file_kind: "any",
  max_files: 1,
  max_size_mb: 5,
  visible_when: null,
  saved: false,
});

// One column of a repeater row. Like a field, its key follows its label until
// saved, since stored rows are keyed by it. It starts with a placeholder key
// (column_N): unlabelled columns sharing an empty key would read and write the
// same cell in every row.
export const emptySubfield = (key = "column_1") => ({ uid: nextUid(), key, label: "", type: "text", required: false, optionsText: "", saved: false });

export const emptyAction = () => ({ label: "", field: "", value: "", color: "primary", confirm: "", visible_when: null });

// A clone keeps the whole setup but becomes a new module: no id, a new name,
// and field keys that may still follow label edits (it has no records yet).
export const draftFromModule = (module, { clone = false } = {}) => {
  const settings = settingsOf(module);
  if (!module) {
    return { id: null, name: "", cloneOf: null, fields: [emptyField()], actions: [], settings: { ...settings, list: { ...settings.list, columns: [] } } };
  }
  return {
    id: clone ? null : module.id,
    name: clone ? `${module.name} Copy` : module.name,
    cloneOf: clone ? module.name : null,
    fields: module.fields.map((field) => ({
      ...emptyField(),
      ...field,
      uid: nextUid(),
      section: sectionKeyOf(field, settings.sections),
      optionsText: (field.options ?? []).join(", "),
      option_colors: field.option_colors && !Array.isArray(field.option_colors) ? field.option_colors : {},
      source: field.source ?? "",
      placeholder: field.placeholder ?? "",
      help: field.help ?? "",
      min: field.min ?? "",
      max: field.max ?? "",
      max_length: field.max_length ?? "",
      subfields: (field.subfields ?? []).map((sub) => ({
        ...emptySubfield(),
        ...sub,
        optionsText: (sub.options ?? []).join(", "),
        saved: !clone,
      })),
      max_rows: field.max_rows ?? "",
      saved: !clone,
    })),
    actions: (module.actions ?? []).map((action) => ({ ...emptyAction(), ...action, confirm: action.confirm ?? "" })),
    settings: {
      ...settings,
      sections: settings.sections.map((section) => ({ ...section })),
      list: { ...settings.list },
      view: { ...settings.view },
    },
  };
};

// A template is a ready-made module definition, so using one is a clone of it
// that keeps the template's own name, numbered if a module already has it.
export const draftFromTemplate = (template, takenNames = []) => {
  const base = template.module.name;
  let name = base;
  for (let n = 2; takenNames.includes(name); n++) name = `${base} ${n}`;
  return { ...draftFromModule(template.module, { clone: true }), name, cloneOf: null, templateOf: template.name };
};

// Points every reference to a field key (buttons, conditions, list columns,
// filters, sort, board) through `map`; a null result means the field is gone.
const remapRefs = (draft, map) => {
  const ref = (key) => (key ? map(key) : key);
  const condition = (rule) => {
    if (!rule?.field) return rule;
    const key = map(rule.field);
    return key ? { ...rule, field: key } : null;
  };
  const { list } = draft.settings;
  return {
    ...draft,
    fields: draft.fields.map((field) => ({ ...field, visible_when: condition(field.visible_when) })),
    actions: draft.actions
      .filter((action) => !action.field || ref(action.field))
      .map((action) => ({ ...action, field: ref(action.field), visible_when: condition(action.visible_when) })),
    settings: {
      ...draft.settings,
      list: {
        ...list,
        columns: list.columns.map(ref).filter(Boolean),
        filters: list.filters.map(ref).filter(Boolean),
        sort_field: ["", "code", "created"].includes(list.sort_field) ? list.sort_field : ref(list.sort_field) ?? "",
        board_field: ref(list.board_field) ?? "",
      },
    },
  };
};

export const updateField = (draft, index, patch) => {
  const field = draft.fields[index];
  const next = { ...field, ...patch };
  if (patch.label !== undefined && !field.saved) next.key = uniqueKey(toKey(patch.label), draft.fields, index);
  if (patch.type !== undefined && patch.type !== field.type) {
    // A default typed for the old type rarely fits the new one.
    next.default = null;
    // A new repeater starts with one column and the whole row's width.
    if (patch.type === "repeater") {
      next.width = "full";
      if (!next.subfields.length) next.subfields = [emptySubfield()];
    }
  }
  const updated = { ...draft, fields: draft.fields.map((item, i) => (i === index ? next : item)) };
  return field.key && next.key !== field.key ? remapRefs(updated, (key) => (key === field.key ? next.key : key)) : updated;
};

export const removeField = (draft, index) => {
  const { key } = draft.fields[index];
  const updated = { ...draft, fields: draft.fields.filter((_, i) => i !== index) };
  return key ? remapRefs(updated, (ref) => (ref === key ? null : ref)) : updated;
};

// The copy lands right below the original as a new field with its own key.
export const cloneField = (draft, index) => {
  const original = draft.fields[index];
  const label = `${original.label} Copy`.trim();
  const copy = { ...original, uid: nextUid(), label, key: uniqueKey(toKey(label), draft.fields), saved: false };
  return { ...draft, fields: [...draft.fields.slice(0, index + 1), copy, ...draft.fields.slice(index + 1)] };
};

export const addField = (draft, section) => ({ ...draft, fields: [...draft.fields, emptyField(section)] });

const setSubfields = (draft, index, change) => ({
  ...draft,
  fields: draft.fields.map((field, i) => (i === index ? { ...field, subfields: change(field.subfields) } : field)),
});

export const addSubfield = (draft, index) =>
  setSubfields(draft, index, (subs) => [...subs, emptySubfield(uniqueKey(`column_${subs.length + 1}`, subs))]);

export const removeSubfield = (draft, index, subIndex) =>
  setSubfields(draft, index, (subs) => subs.filter((_, i) => i !== subIndex));

export const updateSubfield = (draft, index, subIndex, patch) =>
  setSubfields(draft, index, (subs) =>
    subs.map((sub, i) => {
      if (i !== subIndex) return sub;
      const next = { ...sub, ...patch };
      if (patch.label !== undefined && !sub.saved) next.key = uniqueKey(toKey(patch.label), subs, i);
      return next;
    })
  );

// Puts the field at `from` before the field now at `toIndex` (or last, when
// toIndex is the list length) and into `section`. Fields render grouped by
// section, so only the order within a section is visible.
export const moveField = (draft, from, toIndex, section) => {
  const fields = [...draft.fields];
  const [field] = fields.splice(from, 1);
  fields.splice(from < toIndex ? toIndex - 1 : toIndex, 0, { ...field, section });
  return { ...draft, fields };
};

// One step up or down among the fields of the same section.
export const nudgeField = (draft, index, step) => {
  const { section } = draft.fields[index];
  const peers = draft.fields.map((field, i) => (field.section === section ? i : -1)).filter((i) => i >= 0);
  const target = peers[peers.indexOf(index) + step];
  if (target === undefined) return draft;
  return moveField(draft, index, step < 0 ? target : target + 1, section);
};

const setSections = (draft, sections) => ({ ...draft, settings: { ...draft.settings, sections } });

export const addSection = (draft) =>
  setSections(draft, [...draft.settings.sections, { key: newSectionKey(), title: "New Section", columns: 2 }]);

export const updateSection = (draft, key, patch) =>
  setSections(draft, draft.settings.sections.map((section) => (section.key === key ? { ...section, ...patch } : section)));

export const nudgeSection = (draft, index, step) => {
  const sections = [...draft.settings.sections];
  if (!sections[index + step]) return draft;
  [sections[index], sections[index + step]] = [sections[index + step], sections[index]];
  return setSections(draft, sections);
};

// The last section stays; a removed section's fields move to the first one left.
export const removeSection = (draft, key) => {
  const sections = draft.settings.sections.filter((section) => section.key !== key);
  if (!sections.length) return draft;
  const fallback = sections[0].key;
  return {
    ...setSections(draft, sections),
    fields: draft.fields.map((field) => (field.section === key ? { ...field, section: fallback } : field)),
  };
};

export const setListSetting = (draft, patch) => ({
  ...draft,
  settings: { ...draft.settings, list: { ...draft.settings.list, ...patch } },
});

// What the API takes: builder-only props dropped, text options split.
export const toPayload = (draft) => ({
  name: draft.name.trim(),
  fields: draft.fields.map(({ uid, optionsText, saved, in_list, ...field }) => ({
    ...field,
    label: field.label.trim(),
    options: OPTION_TYPES.includes(field.type) ? splitOptions(optionsText) : [],
    source: field.type === "autocomplete" ? field.source : null,
    min: field.min === "" ? null : field.min,
    max: field.max === "" ? null : field.max,
    max_length: field.max_length === "" ? null : field.max_length,
    subfields:
      field.type === "repeater"
        ? field.subfields.map(({ key, label, type, required, optionsText: subOptions }) => ({
            key,
            label: label.trim(),
            type,
            required,
            options: type === "select" ? splitOptions(subOptions) : [],
          }))
        : [],
    max_rows: field.type === "repeater" && field.max_rows !== "" ? field.max_rows : null,
    file_kind: field.type === "attachment" ? field.file_kind || "any" : null,
    max_files: field.type === "attachment" ? Number(field.max_files) || 1 : null,
    max_size_mb: field.type === "attachment" ? Number(field.max_size_mb) || 5 : null,
  })),
  actions: draft.actions.map((action) => ({ ...action, label: action.label.trim() })),
  settings: draft.settings,
});

// The draft shaped like a saved module, for the live preview. Fields still
// without a label have no key yet, so they are left out.
export const previewModule = (draft) => ({
  name: draft.name,
  fields: toPayload(draft).fields.filter((field) => field.key),
  settings: draft.settings,
  actions: [],
});
